/**
 * Management View analytics — a line-for-line port of the original app's `mvCompute` and helpers.
 * Every rule here is listed in `definitions.ts` and shown on the page ("How these figures are calculated").
 * Do not change a rule without updating both, and the parity tests.
 */
import type { Backup, Member, Project, ProjectUpdate, WorkLog } from '@/types/models';
import { addDays, dayDiff, daysLeft, isValidDate, round2, today } from '../format';
import { kindOf, parseMetricValue } from '../metrics';
import { ACTIVE_STATUSES, deadline, STALE_DAYS } from '../projects';
import {
  GENERAL,
  UNKNOWN,
  type ExtensionRow,
  type ManagementAnalytics,
  type ManagementFilter,
  type MemberAggregate,
  type MetricSeries,
  type ProjectAggregate,
  type ProjectRow,
} from './types';

export const defaultManagementFilter = (): ManagementFilter => ({ period: 'last30', from: '', to: '', projects: [], members: [], statuses: [], leads: [] });

/** Lead / Owner filter key: lowercase trimmed name, or `__none`. */
export const leadKey = (p: Pick<Project, 'lead'>): string => String(p.lead || '').trim().toLowerCase() || '__none';

export const inPeriod = (d: string, [a, b]: [string, string]): boolean => (!a && !b ? true : isValidDate(d) && (!a || d >= a) && (!b || d <= b));

export function managementRange(f: Pick<ManagementFilter, 'period' | 'from' | 'to'>): [string, string] {
  const t = today();
  switch (f.period) {
    case 'today':
      return [t, t];
    case 'last7':
      return [addDays(t, -6), t];
    case 'last30':
      return [addDays(t, -29), t];
    case 'month':
      return [t.slice(0, 8) + '01', t];
    case 'prevmonth': {
      const e = addDays(t.slice(0, 8) + '01', -1);
      return [e.slice(0, 8) + '01', e];
    }
    case 'quarter': {
      const m = Math.floor((Number(t.slice(5, 7)) - 1) / 3) * 3 + 1;
      return [`${t.slice(0, 4)}-${String(m).padStart(2, '0')}-01`, t];
    }
    case 'custom':
      return [isValidDate(f.from) ? f.from : '', isValidDate(f.to) ? f.to : ''];
    default:
      return ['', ''];
  }
}

const hoursOf = (l: WorkLog): number => Number(l.hours) || 0; // same as the rest of the app

/** Records sharing an id are counted once (a hand-edited backup can repeat them). */
function uniqById<T extends { id?: string | null }>(a: T[] | undefined): T[] {
  const s = new Set<string>();
  return (Array.isArray(a) ? a : []).filter((x) => {
    if (!x || typeof x !== 'object') return false;
    if (x.id == null) return true;
    if (s.has(x.id)) return false;
    s.add(x.id);
    return true;
  });
}

export function healthOf(r: Pick<ProjectRow, 'status' | 'daysLeft' | 'stale' | 'sinceUpdate' | 'extensionCount'>): ProjectRow['health'] {
  if (r.status === 'Completed') return { level: 'done', reasons: ['Status is Completed — not assessed'] };
  const c: string[] = [];
  const a: string[] = [];
  const d = (n: number) => `${n} day${n === 1 ? '' : 's'}`;
  if (r.status === 'Delayed') c.push('Status is Delayed');
  if (r.daysLeft !== null && r.daysLeft < 0) c.push(`Past deadline by ${d(-r.daysLeft)}`);
  if (r.stale) a.push(r.sinceUpdate === null ? 'No Project Log yet' : `No Project Log for ${d(r.sinceUpdate)}`);
  if (r.daysLeft !== null && r.daysLeft >= 0 && r.daysLeft <= 7) a.push(`Deadline in ${d(r.daysLeft)}`);
  if (r.status === 'On Hold') a.push('Status is On Hold');
  if (r.extensionCount >= 2) a.push(`Deadline changed ${r.extensionCount} times`);
  return c.length ? { level: 'critical', reasons: [...c, ...a] } : a.length ? { level: 'attention', reasons: a } : { level: 'healthy', reasons: ['No health rule triggered'] };
}

type Source = Pick<Backup, 'projects' | 'members' | 'logs' | 'updates'>;

export function computeManagementAnalytics(src: Source, filter: ManagementFilter): ManagementAnalytics {
  const f = filter;
  const T = today();
  const R = managementRange(f);
  const projects = uniqById<Project>(src.projects);
  const members = uniqById<Member>(src.members);
  const logs = uniqById<WorkLog>(src.logs);
  const ups = uniqById<ProjectUpdate>(src.updates);
  const pBy = new Map(projects.map((p) => [p.id, p]));
  const mBy = new Map(members.map((m) => [m.id, m]));
  const selP = new Set(f.projects.filter((id) => id === GENERAL || pBy.has(id)));
  const selM = new Set(f.members.filter((id) => mBy.has(id)));
  const selS = new Set<string>(f.statuses);
  const selL = new Set(f.leads);
  const projF = !!(selP.size || selS.size || selL.size);

  let P = projects.filter((p) => (!selP.size || selP.has(p.id)) && (!selS.size || selS.has(p.status)) && (!selL.size || selL.has(leadKey(p))));
  if (selM.size) {
    const touched = new Set(logs.filter((l) => selM.has(l.memberId)).map((l) => l.projectId));
    P = P.filter((p) => (p.members || []).some((id) => selM.has(id)) || touched.has(p.id));
  }
  const pSet = new Set(P.map((p) => p.id));
  const general = (!selP.size || selP.has(GENERAL)) && !selS.size && !selL.size;
  const inScope = (l: WorkLog) => (!projF || (l.projectId ? pSet.has(l.projectId) : general)) && (!selM.size || selM.has(l.memberId));

  const days = R[0] && R[1] ? dayDiff(R[0], R[1]) + 1 : null;
  const prevR: [string, string] | null = days ? [addDays(R[0], -days), addDays(R[0], -1)] : null;
  const SL = logs.filter(inScope);
  const L = SL.filter((l) => inPeriod(l.date, R));
  const LP = prevR ? SL.filter((l) => inPeriod(l.date, prevR)) : [];

  // One Project Log per project per day; if a backup holds two, the later record wins.
  const um = new Map<string, ProjectUpdate>();
  ups.forEach((u) => {
    if (pSet.has(u.projectId)) um.set(u.projectId + '|' + u.date, u);
  });
  const SU = [...um.values()];
  const U = SU.filter((u) => inPeriod(u.date, R));
  const UP = prevR ? SU.filter((u) => inPeriod(u.date, prevR)) : [];

  // Latest Project Log per project — all time (the Dashboard's "Last project log").
  const lastUp = new Map<string, string>();
  ups.forEach((u) => {
    const c = lastUp.get(u.projectId);
    if (isValidDate(u.date) && (!c || u.date > c)) lastUp.set(u.projectId, u.date);
  });

  const pk = (l: WorkLog) => (l.projectId ? (pBy.has(l.projectId) ? l.projectId : UNKNOWN) : GENERAL);
  const mk = (l: WorkLog) => (mBy.has(l.memberId) ? l.memberId : UNKNOWN);
  const byP = new Map<string, { h: number; e: number; days: Set<string>; mem: Set<string> }>();
  const byM = new Map<string, { h: number; e: number; days: Set<string>; projs: Set<string> }>();
  const matrix: Record<string, number> = {};
  const lastWorked: Record<string, string> = {};
  L.forEach((l) => {
    const h = hoursOf(l);
    const p = pk(l);
    const m = mk(l);
    const ok = isValidDate(l.date);
    const a = byP.get(p) || { h: 0, e: 0, days: new Set<string>(), mem: new Set<string>() };
    a.h += h;
    a.e++;
    if (ok) a.days.add(l.date);
    a.mem.add(m);
    byP.set(p, a);
    const b = byM.get(m) || { h: 0, e: 0, days: new Set<string>(), projs: new Set<string>() };
    b.h += h;
    b.e++;
    if (ok) b.days.add(l.date);
    b.projs.add(p);
    byM.set(m, b);
    matrix[m + '\u0001' + p] = (matrix[m + '\u0001' + p] || 0) + h;
  });
  SL.forEach((l) => {
    const m = mk(l);
    if (isValidDate(l.date) && l.date > (lastWorked[m] || '')) lastWorked[m] = l.date;
  });

  const upN = new Map<string, number>();
  const upDays = new Map<string, Set<string>>();
  U.forEach((u) => {
    upN.set(u.projectId, (upN.get(u.projectId) || 0) + 1);
    if (isValidDate(u.date)) {
      if (!upDays.has(u.projectId)) upDays.set(u.projectId, new Set());
      upDays.get(u.projectId)!.add(u.date);
    }
  });

  const rows: ProjectRow[] = P.map((p) => {
    const a = byP.get(p.id);
    const dl = deadline(p);
    const n0 = dl ? daysLeft(dl) : null;
    const n = n0 !== null && Number.isFinite(n0) ? n0 : null;
    const s0 = lastUp.has(p.id) ? dayDiff(lastUp.get(p.id)!, T) : null;
    const s = s0 !== null && Number.isFinite(s0) ? s0 : null;
    const active = ACTIVE_STATUSES.includes(p.status);
    const ad = new Set([...(a ? a.days : []), ...(upDays.get(p.id) || [])]);
    const ext = (p.extensions || []).filter((e) => e && isValidDate(e.from) && isValidDate(e.to));
    const stale = active && (s === null || s >= STALE_DAYS);
    const status = p.status;
    const bucket: ProjectRow['bucket'] = status === 'Completed' ? 'completed' : n === null ? 'none' : n < 0 ? 'overdue' : n <= 7 ? 'due7' : n <= 30 ? 'due30' : 'later';
    const overdue = status !== 'Completed' && n !== null && n < 0;
    const r: ProjectRow = {
      id: p.id,
      project: p,
      name: p.name || '(Untitled)',
      service: p.service || '',
      lead: String(p.lead || '').trim(),
      status,
      active,
      hours: a ? a.h : 0,
      entries: a ? a.e : 0,
      lastUpdate: lastUp.get(p.id) || '',
      sinceUpdate: s,
      stale,
      deadline: dl,
      daysLeft: n,
      extensionCount: (p.extensions || []).length,
      daysAdded: ext.reduce((x, e) => x + dayDiff(e.from, e.to), 0),
      latestReason: (ext[ext.length - 1] || { reason: '' }).reason || '',
      updatesInPeriod: upN.get(p.id) || 0,
      activeDays: ad.size,
      bucket,
      overdue,
      onTime: status !== 'Completed' && n !== null && n >= 0,
      health: { level: 'healthy', reasons: [] },
      risk: 'na',
    };
    r.health = healthOf(r);
    r.risk = !active ? 'na' : !stale ? 'low' : s === null || overdue || bucket === 'due7' ? 'high' : 'medium';
    return r;
  });

  const teamIds = new Set(projF ? [...P.flatMap((p) => p.members || []), ...L.map((l) => l.memberId)] : members.map((m) => m.id));
  const team = [...teamIds].filter((id) => mBy.has(id) && (!selM.size || selM.has(id))).map((id) => mBy.get(id)!);
  const act = [...byM].filter(([id, x]) => id !== UNKNOWN && x.h > 0);
  const H = L.reduce((s, l) => s + hoursOf(l), 0);
  const actH = act.reduce((s, [, x]) => s + x.h, 0);
  const pm = new Map<string, number>();
  LP.forEach((l) => {
    if (mBy.has(l.memberId)) pm.set(l.memberId, (pm.get(l.memberId) || 0) + hoursOf(l));
  });
  const previous = { hours: LP.reduce((s, l) => s + hoursOf(l), 0), activeMembers: [...pm.values()].filter((h) => h > 0).length, projectsUpdated: new Set(UP.map((u) => u.projectId)).size };

  // Project metrics: each project's own labels; numeric values only.
  const mets = new Map<string, Omit<MetricSeries, 'first' | 'last' | 'unit' | 'percentage' | 'summed' | 'total' | 'change'>>();
  [...U]
    .filter((u) => isValidDate(u.date))
    .sort((a, b) => (a.date < b.date ? -1 : a.date > b.date ? 1 : 0))
    .forEach((u) => {
      const p = pBy.get(u.projectId)!;
      (Array.isArray(u.metrics) ? u.metrics : []).forEach((m) => {
        const lab = String((m && m.label) || '').trim();
        if (!lab) return;
        const k = u.projectId + '\u0001' + lab;
        let x = mets.get(k);
        if (!x) {
          x = { key: k, projectId: u.projectId, label: lab, kind: kindOf(p, lab), points: [], textValues: 0 };
          mets.set(k, x);
        }
        const v = parseMetricValue(m.value);
        if (!v) {
          if (String(m.value ?? '').trim()) x.textValues++;
          return;
        }
        const last = x.points[x.points.length - 1];
        if (last && last.date === u.date) x.points.pop();
        x.points.push({ date: u.date, value: v.n, unit: v.u });
      });
    });
  const metrics: MetricSeries[] = [...mets.values()].map((x) => {
    const first = x.points[0] || null;
    const last = x.points[x.points.length - 1] || null;
    const unit = last ? last.unit : '';
    const percentage = unit === '%';
    return {
      ...x,
      first,
      last,
      unit,
      percentage,
      summed: x.kind === 'daily' && !percentage,
      total: round2(x.points.reduce((s, q) => s + q.value, 0)),
      change: x.points.length > 1 && first && last ? round2(last.value - first.value) : null,
    };
  });

  const extensions: ExtensionRow[] = P.flatMap((p) =>
    (p.extensions || [])
      .filter((e) => e && isValidDate(e.from) && isValidDate(e.to))
      .map((e) => ({ projectId: p.id, projectName: p.name, on: e.on || '', from: e.from, to: e.to, reason: String(e.reason || ''), days: dayDiff(e.from, e.to) })),
  );
  const extensionsInPeriod = extensions.filter((e) => inPeriod(e.on, R));

  const dataQuality = qualityNotes(src, { projects, members, logs, ups, pBy, mBy });

  const toAgg = <T extends { h: number; e: number; days: Set<string> }>(x: T) => ({ hours: x.h, entries: x.e, days: [...x.days] });
  const byProject: Record<string, ProjectAggregate> = {};
  byP.forEach((x, k) => (byProject[k] = { ...toAgg(x), members: [...x.mem] }));
  const byMember: Record<string, MemberAggregate> = {};
  byM.forEach((x, k) => (byMember[k] = { ...toAgg(x), projects: [...x.projs] }));

  return {
    filter: f,
    today: T,
    range: R,
    days,
    previousRange: prevR,
    allProjects: projects,
    allMembers: members,
    projectFilterOn: projF,
    memberFilterOn: selM.size > 0,
    rows,
    team,
    logs: L,
    scopeLogs: SL,
    updates: U,
    byProject,
    byMember,
    matrix,
    lastWorked,
    activeMemberIds: act.map(([id]) => id),
    hours: H,
    activeHours: actH,
    previous,
    metrics,
    extensions,
    extensionsInPeriod,
    dataQuality,
  };
}

function qualityNotes(
  src: Source,
  x: { projects: Project[]; members: Member[]; logs: WorkLog[]; ups: ProjectUpdate[]; pBy: Map<string, Project>; mBy: Map<string, Member> },
): string[] {
  const pl = (n: number, one: string, many?: string) => `${n.toLocaleString('en-IN')} ${n === 1 ? one : many || one + 's'}`;
  const cnt = (a: unknown[] | undefined, b: unknown[]) => (Array.isArray(a) ? a.length : 0) - b.length;
  const out: string[] = [];
  const dupes = cnt(src.logs, x.logs) + cnt(src.updates, x.ups) + cnt(src.projects, x.projects) + cnt(src.members, x.members);
  if (dupes) out.push(`${pl(dupes, 'record')} with a repeated ID ${dupes === 1 ? 'was' : 'were'} counted once.`);
  const bad = x.logs.filter((l) => !isValidDate(l.date)).length;
  if (bad) out.push(`${pl(bad, 'work-log entry', 'work-log entries')} ${bad === 1 ? 'has' : 'have'} no valid date and ${bad === 1 ? 'is' : 'are'} counted under All time only.`);
  const om = x.logs.filter((l) => !x.mBy.has(l.memberId)).length;
  if (om) out.push(`${pl(om, 'work-log entry', 'work-log entries')} ${om === 1 ? 'refers' : 'refer'} to a member that no longer exists and ${om === 1 ? 'is' : 'are'} shown as Unknown member.`);
  const op = x.logs.filter((l) => l.projectId && !x.pBy.has(l.projectId)).length;
  if (op) out.push(`${pl(op, 'work-log entry', 'work-log entries')} ${op === 1 ? 'refers' : 'refer'} to a project that no longer exists and ${op === 1 ? 'is' : 'are'} shown as Unknown project.`);
  const nh = x.logs.filter((l) => !Number.isFinite(Number(l.hours))).length;
  if (nh) out.push(`${pl(nh, 'work-log entry', 'work-log entries')} ${nh === 1 ? 'has' : 'have'} non-numeric hours, counted as 0.`);
  const known = x.ups.filter((u) => x.pBy.has(u.projectId));
  const du = known.length - new Set(known.map((u) => u.projectId + '|' + u.date)).size;
  if (du) out.push(`${pl(du, 'duplicate Project Log')} (same project and date) ${du === 1 ? 'was' : 'were'} ignored; the later record is used.`);
  return out;
}

/* ---------- activity series (Daily activity trend, project hours per day) ---------- */

export type Granularity = 'day' | 'week' | 'month';
export interface ActivityBucket {
  key: string;
  from: string;
  to: string;
  label: string;
  title: string;
  hours: number;
  entries: number;
  members: number;
  projects: number;
  updates: number;
}
export const TREND_METRICS: [keyof Pick<ActivityBucket, 'hours' | 'entries' | 'projects' | 'members' | 'updates'>, string][] = [
  ['hours', 'Hours logged'],
  ['entries', 'Work-log entries'],
  ['projects', 'Projects touched'],
  ['members', 'Members active'],
  ['updates', 'Project updates'],
];

/** Activity per day (or week over 92 days, month over 2 years); days with no records are 0. */
export function activitySeries(
  a: Pick<ManagementAnalytics, 'range' | 'today' | 'allMembers'> & { logs: WorkLog[]; updates: ProjectUpdate[] },
): { granularity: Granularity; buckets: ActivityBucket[] } {
  let [from, to] = a.range;
  const ds = [...a.logs.map((l) => l.date), ...a.updates.map((u) => u.date)].filter(isValidDate).sort();
  if (!from) from = ds[0];
  if (!to) to = ds.length && ds[ds.length - 1] > a.today ? ds[ds.length - 1] : a.today;
  if (!from || from > to) return { granularity: 'day', buckets: [] };
  const span = dayDiff(from, to) + 1;
  const g: Granularity = span <= 92 ? 'day' : span <= 731 ? 'week' : 'month';
  const kf = (d: string) => (g === 'day' ? d : g === 'week' ? addDays(d, -((new Date(d + 'T00:00:00').getDay() + 6) % 7)) : d.slice(0, 7));
  const nextM = (d: string) => {
    const x = new Date(d.slice(0, 7) + '-01T00:00:00');
    x.setMonth(x.getMonth() + 1);
    return `${x.getFullYear()}-${String(x.getMonth() + 1).padStart(2, '0')}-01`;
  };
  const memberIds = new Set(a.allMembers.map((m) => m.id));
  const B = new Map<string, { k: string; from: string; to: string; h: number; e: number; mem: Map<string, number>; proj: Set<string>; up: number }>();
  for (let d = g === 'day' ? from : g === 'week' ? kf(from) : from.slice(0, 7) + '-01'; d <= to; d = g === 'month' ? nextM(d) : addDays(d, g === 'week' ? 7 : 1)) {
    const k = kf(d);
    if (!B.has(k)) B.set(k, { k, from: g === 'day' ? k : g === 'week' ? (k < from ? from : k) : k + '-01' < from ? from : k + '-01', to: '', h: 0, e: 0, mem: new Map(), proj: new Set(), up: 0 });
  }
  B.forEach((x) => {
    if (g === 'day') x.to = x.k;
    else if (g === 'week') x.to = addDays(x.k, 6);
    else {
      const y = new Date(x.k + '-01T00:00:00');
      y.setMonth(y.getMonth() + 1);
      y.setDate(0);
      x.to = `${y.getFullYear()}-${String(y.getMonth() + 1).padStart(2, '0')}-${String(y.getDate()).padStart(2, '0')}`;
    }
    if (x.to > to) x.to = to;
  });
  a.logs.forEach((l) => {
    if (!isValidDate(l.date)) return;
    const x = B.get(kf(l.date));
    if (!x) return;
    x.h += hoursOf(l);
    x.e++;
    if (l.projectId) x.proj.add(l.projectId);
    if (memberIds.has(l.memberId)) x.mem.set(l.memberId, (x.mem.get(l.memberId) || 0) + hoursOf(l));
  });
  a.updates.forEach((u) => {
    if (!isValidDate(u.date)) return;
    const x = B.get(kf(u.date));
    if (!x) return;
    x.up++;
    x.proj.add(u.projectId);
  });
  const dm = (d: string) => `${d.slice(8, 10)}/${d.slice(5, 7)}`;
  const fd = (d: string) => `${d.slice(8, 10)}/${d.slice(5, 7)}/${d.slice(0, 4)}`;
  const label = (k: string) =>
    g === 'day' ? dm(k) : g === 'week' ? 'w/c ' + dm(k) : new Date(k + '-01T00:00:00').toLocaleString('en-GB', { month: 'short', year: 'numeric' });
  return {
    granularity: g,
    buckets: [...B.values()].map((x) => ({
      key: x.k,
      from: x.from,
      to: x.to,
      label: label(x.k),
      title: g === 'day' ? fd(x.k) : `${fd(x.from)} – ${fd(x.to)}`,
      hours: round2(x.h),
      entries: x.e,
      members: [...x.mem.values()].filter((h) => h > 0).length,
      projects: x.proj.size,
      updates: x.up,
    })),
  };
}
